import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, LogOut, Phone, Mail, Calendar, Clock, Trash2, StickyNote, RefreshCw,
} from "lucide-react";
import { BRAND, IMAGES } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "be_admin_key";

const STATUSES = [
  { value: "new", label: "New", cls: "bg-[#c96c31]/15 text-[#c96c31]" },
  { value: "confirmed", label: "Confirmed", cls: "bg-emerald-600/15 text-emerald-700" },
  { value: "completed", label: "Completed", cls: "bg-[#2c1e16]/10 text-[#2c1e16]/70" },
  { value: "cancelled", label: "Cancelled", cls: "bg-red-600/10 text-red-600" },
];

const statusCls = (s) => STATUSES.find((x) => x.value === s)?.cls || STATUSES[0].cls;

const KeyGate = ({ onUnlock }) => {
  const [key, setKey] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    setLoading(true);
    try {
      await axios.get(`${API}/bookings`, { headers: { "X-Admin-Key": key.trim() } });
      localStorage.setItem(KEY_STORAGE, key.trim());
      onUnlock(key.trim());
    } catch {
      toast.error("Invalid admin key.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#2c1e16] px-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-3xl bg-[#f9f6f0] p-8 md:p-10 text-center">
        <img src={IMAGES.logo} alt={BRAND.name} className="mx-auto h-14 w-14 object-contain" />
        <h1 className="font-display text-3xl text-[#2c1e16] mt-4">Owner Access</h1>
        <p className="font-body text-sm text-[#2c1e16]/60 mt-2">Enter your admin key to manage bookings.</p>
        <div className="mt-6 flex items-center gap-2 border-b border-[#2c1e16]/20 focus-within:border-[#c96c31] transition-colors">
          <Lock size={16} className="text-[#2c1e16]/40 shrink-0" />
          <input
            type="password"
            data-testid="admin-key-input"
            className="w-full bg-transparent py-3 font-body text-[#2c1e16] placeholder:text-[#2c1e16]/40 focus:outline-none"
            placeholder="Admin key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
        </div>
        <button
          type="submit"
          data-testid="admin-login-btn"
          disabled={loading}
          className="mt-6 w-full rounded-full bg-[#c96c31] px-8 py-3.5 font-body text-sm font-medium uppercase tracking-wider text-[#f9f6f0] hover:bg-[#b35d27] transition-colors disabled:opacity-60"
        >
          {loading ? "Checking…" : "Unlock Dashboard"}
        </button>
      </form>
    </div>
  );
};

const BookingCard = ({ b, adminKey, onChanged }) => {
  const [busy, setBusy] = useState(false);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/bookings/${b.id}`, { status }, { headers: { "X-Admin-Key": adminKey } });
      toast.success(`Marked as ${status}.`);
      onChanged();
    } catch {
      toast.error("Update failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete booking from ${b.name}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/bookings/${b.id}`, { headers: { "X-Admin-Key": adminKey } });
      toast.success("Booking deleted.");
      onChanged();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`booking-card-${b.id}`} className="rounded-2xl border border-[#2c1e16]/10 bg-white p-6 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-2xl text-[#2c1e16]">{b.name}</p>
          <p className="font-body text-sm text-[#c96c31] mt-0.5">{b.service}</p>
        </div>
        <span data-testid={`booking-status-${b.id}`} className={`rounded-full px-3 py-1 font-body text-xs uppercase tracking-wider ${statusCls(b.status)}`}>
          {b.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 font-body text-sm text-[#2c1e16]/80">
        <a href={`tel:${b.phone}`} className="flex items-center gap-2 hover:text-[#c96c31]"><Phone size={14} className="text-[#c96c31]" />{b.phone}</a>
        <span className="flex items-center gap-2 truncate"><Mail size={14} className="text-[#c96c31]" />{b.email || "—"}</span>
        <span className="flex items-center gap-2"><Calendar size={14} className="text-[#c96c31]" />{b.preferred_date}</span>
        <span className="flex items-center gap-2"><Clock size={14} className="text-[#c96c31]" />{b.preferred_time}</span>
      </div>

      {b.notes && (
        <p className="flex items-start gap-2 font-body text-sm text-[#2c1e16]/60 bg-[#f9f6f0] rounded-xl px-4 py-3">
          <StickyNote size={14} className="text-[#c96c31] mt-0.5 shrink-0" />{b.notes}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {STATUSES.filter((s) => s.value !== b.status).map((s) => (
          <button
            key={s.value}
            data-testid={`set-status-${s.value}-${b.id}`}
            disabled={busy}
            onClick={() => setStatus(s.value)}
            className="rounded-full border border-[#2c1e16]/15 px-4 py-1.5 font-body text-xs uppercase tracking-wider text-[#2c1e16]/70 hover:border-[#c96c31] hover:text-[#c96c31] transition-colors disabled:opacity-50"
          >
            {s.label}
          </button>
        ))}
        <button
          data-testid={`delete-booking-${b.id}`}
          disabled={busy}
          onClick={remove}
          className="ml-auto grid place-items-center h-8 w-8 rounded-full text-red-500/70 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
          title="Delete"
        >
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
};

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings`, { headers: { "X-Admin-Key": key } });
      setBookings(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(KEY_STORAGE);
        setAdminKey("");
        toast.error("Session expired. Please enter your key again.");
      } else {
        toast.error("Could not load bookings.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminKey) load(adminKey);
  }, [adminKey, load]);

  if (!adminKey) return <KeyGate onUnlock={setAdminKey} />;

  const counts = bookings.reduce((acc, b) => ({ ...acc, [b.status]: (acc[b.status] || 0) + 1 }), {});
  const visible = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  const logout = () => {
    localStorage.removeItem(KEY_STORAGE);
    setAdminKey("");
  };

  return (
    <div className="min-h-screen bg-[#f3ebe1]" data-testid="admin-dashboard">
      <header className="bg-[#2c1e16]">
        <div className="mx-auto max-w-[1200px] px-4 md:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt={BRAND.name} className="h-10 w-10 object-contain" />
            <div>
              <p className="font-display text-xl text-[#f9f6f0] leading-none">Bookings Dashboard</p>
              <p className="font-body text-xs text-[#c96c31] mt-1 uppercase tracking-widest">{BRAND.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => load(adminKey)}
              className="grid place-items-center h-10 w-10 rounded-full text-[#f9f6f0]/70 hover:text-[#c96c31] transition-colors"
              title="Refresh"
            >
              <RefreshCw size={17} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-[#f9f6f0]/25 px-5 py-2 font-body text-xs uppercase tracking-wider text-[#f9f6f0] hover:border-[#c96c31] hover:text-[#c96c31] transition-colors"
            >
              <LogOut size={14} /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-4 md:px-8 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {[{ value: "all", label: "All" }, ...STATUSES].map((s) => {
            const count = s.value === "all" ? bookings.length : counts[s.value] || 0;
            const active = filter === s.value;
            return (
              <button
                key={s.value}
                data-testid={`filter-${s.value}`}
                onClick={() => setFilter(s.value)}
                className={`rounded-2xl border p-4 text-left transition-colors ${
                  active ? "border-[#c96c31] bg-white" : "border-[#2c1e16]/10 bg-white/60 hover:border-[#c96c31]/50"
                }`}
              >
                <p className="font-display text-3xl text-[#2c1e16]">{count}</p>
                <p className={`font-body text-xs uppercase tracking-widest mt-1 ${active ? "text-[#c96c31]" : "text-[#2c1e16]/50"}`}>
                  {s.label}
                </p>
              </button>
            );
          })}
        </div>

        {visible.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-[#2c1e16]/20 py-20 text-center" data-testid="admin-empty-state">
            <p className="font-display text-2xl text-[#2c1e16]/60">No bookings here yet.</p>
            <p className="font-body text-sm text-[#2c1e16]/40 mt-2">New requests will appear as soon as clients book.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {visible.map((b) => (
              <BookingCard key={b.id} b={b} adminKey={adminKey} onChanged={() => load(adminKey)} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
